import React, { useMemo } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { Box, Button, Link } from '@node-space/ui-components'
import { RequestError } from 'components/Errors/RequestError'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { CUSTOMER_AGREEMENTS_API, EmbeddedAgreementStatuses } from '../helpers/constants'
import { EmbeddedAgreementStatus, EmbeddedCustomerAgreement, UIConfig } from '../helpers/types'
import { getAgreementLinks, getListToSentencePrefix } from '../helpers/utils'
import { useCustomerPendingAgreementsMutation } from '../requests/useCustomerPendingAgreementsMutation'
import { DakotaTerms } from '../variations/DakotaTerms'

export interface AgreementsFormProps {
  reference: string
  isDakota?: boolean
  redirectUri?: string
  isUsEntity?: boolean
  agreements: EmbeddedCustomerAgreement[]
  uiConfig?: UIConfig
  onSuccess: () => void
}

export const AgreementsForm = ({
  reference,
  isDakota,
  redirectUri,
  isUsEntity,
  agreements,
  onSuccess,
  uiConfig,
}: AgreementsFormProps) => {
  const { t } = useTranslation()
  const { requestError, resetRequestError, handleRequestError } = useMappedErrorMessage()

  const { mutate: putCustomerAgreements, isPending: isSubmittingAgreements } =
    useCustomerPendingAgreementsMutation()

  const agreementsLinks = useMemo(() => getAgreementLinks(t, agreements), [t, agreements])

  const primaryColor = uiConfig?.primaryColor ? `#${uiConfig.primaryColor}` : undefined

  const goToRedirect = (status: EmbeddedAgreementStatus) => {
    if (!redirectUri) return
    const url = new URL(redirectUri, globalThis.location.origin)
    if (url.protocol !== 'https:') return
    url.searchParams.set('session', reference)
    url.searchParams.set('status', status)
    globalThis.location.assign(url.toString())
  }

  const onCancel = () => {
    goToRedirect(EmbeddedAgreementStatuses.PENDING)
  }

  const onSubmitAgreements = () => {
    resetRequestError()
    putCustomerAgreements(
      { reference },
      {
        onError: error =>
          handleRequestError(error, CUSTOMER_AGREEMENTS_API, 'putCustomerAgreements'),
        onSuccess: () => {
          onSuccess()
          redirectUri && goToRedirect(EmbeddedAgreementStatuses.SIGNED)
        },
      }
    )
  }

  const textClassName = clsx(`md:text-left text-center text-body-regular`, {
    'text-white': uiConfig?.isDarkMode && uiConfig?.isBranded === false,
  })

  return (
    <>
      {requestError?.show && (
        <RequestError
          errorCode={requestError?.errorCode}
          sentryTraceId={requestError?.sentryTraceId}
        />
      )}

      {isDakota ? (
        <DakotaTerms
          agreements={agreementsLinks}
          onSubmitAgreements={onSubmitAgreements}
          isSubmittingAgreements={isSubmittingAgreements}
        />
      ) : (
        <>
          <p className={textClassName}>
            {isUsEntity
              ? t('customerPendingAgreements.introductionUs')
              : t('customerPendingAgreements.introduction')}
            {agreementsLinks?.map(({ label, url }, index) => (
              <span key={label}>
                {getListToSentencePrefix(agreementsLinks?.length, index, t)}
                <span
                  {...(primaryColor && {
                    style: { color: primaryColor },
                  })}
                >
                  <Link
                    href={url}
                    target="_blank"
                    {...(primaryColor && { className: 'text-inherit' })}
                  >
                    {label}
                  </Link>
                </span>
              </span>
            ))}
            .
          </p>
          <p className={textClassName}>{t('customerPendingAgreements.bySubmitting')}</p>
          <Box
            flexCol
            justify={redirectUri ? 'between' : 'center'}
            gapX={8}
            gapY={24}
            paddingT={8}
            className="sm:flex-row-reverse md:pt-4"
          >
            <Button
              loading={isSubmittingAgreements}
              onClick={onSubmitAgreements}
              full
              className="sm:max-w-[318px]"
              {...(primaryColor && {
                style: { backgroundColor: primaryColor },
              })}
            >
              {t('submit')}
            </Button>
            {redirectUri && (
              <Button
                variant={primaryColor ? 'ghost' : 'secondary'}
                disabled={isSubmittingAgreements}
                onClick={onCancel}
                full
                className="sm:max-w-[318px]"
                {...(primaryColor && {
                  style: {
                    color: primaryColor,
                    backgroundColor: '#F5F5F4',
                  },
                })}
              >
                {t('cancel')}
              </Button>
            )}
          </Box>
        </>
      )}
    </>
  )
}
